// Motor de polling genérico e sem dependência de React, generalizando os
// padrões de setInterval + guard por ref + contagem de tentativas já usados
// em src/pages/Query/Queries/index.tsx (exportação de CSV e polling de
// atividades em execução). Usa setTimeout recursivo em vez de setInterval
// para não sobrepor chamadas quando `fetch` demorar mais que `intervalMs`.

export class PollTimeoutError extends Error {
  constructor(message = "Tempo máximo de espera excedido.") {
    super(message);
    this.name = "PollTimeoutError";
  }
}

export class PollError<T> extends Error {
  payload: T;

  constructor(payload: T, message = "A operação terminou em erro.") {
    super(message);
    this.name = "PollError";
    this.payload = payload;
  }
}

export interface PollUntilOptions<T> {
  fetch: () => Promise<T>;
  isDone: (payload: T) => boolean;
  isError: (payload: T) => boolean;
  intervalMs: number;
  maxAttempts: number;
  onTick?: (payload: T, attempt: number) => void;
}

export interface PollHandle<T> {
  promise: Promise<T>;
  cancel: () => void;
}

export function pollUntil<T>(opts: PollUntilOptions<T>): PollHandle<T> {
  const { fetch, isDone, isError, intervalMs, maxAttempts, onTick } = opts;

  let cancelled = false;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let attempt = 0;

  const promise = new Promise<T>((resolve, reject) => {
    const tick = async () => {
      if (cancelled) return;
      attempt += 1;

      try {
        const payload = await fetch();
        if (cancelled) return;

        onTick?.(payload, attempt);

        if (isError(payload)) {
          reject(new PollError(payload));
          return;
        }

        if (isDone(payload)) {
          resolve(payload);
          return;
        }

        if (attempt >= maxAttempts) {
          reject(new PollTimeoutError());
          return;
        }

        timer = setTimeout(tick, intervalMs);
      } catch (err) {
        if (cancelled) return;
        reject(err);
      }
    };

    timer = setTimeout(tick, intervalMs);
  });

  return {
    promise,
    cancel: () => {
      cancelled = true;
      if (timer) {
        clearTimeout(timer);
        timer = null;
      }
    },
  };
}
